import React, { useState, Fragment, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import classNames from 'classnames'
import { OrderHooks } from './OrderHooks'
import { CampaignAddon, Collaboration } from '../../../gql/types'
import { OrderSecondEditsVersionAddon } from './OrderSecondEditsVersionAddon'
import { OrderCaptionsAndMusicAddon } from './OrderCaptionsAndMusicAddon'
//@ts-ignore
import { OrderAspectRatiosAddon } from './OrderAspectRatiosAddon'

interface IProps {
  collaboration: Collaboration
  open: boolean
  setOpen: (event: any) => void
  description?: string
  title?: string
}

export const OrderAddonsVariationsModal: React.FC<IProps> = ({
  collaboration,
  open,
  setOpen,
  title,
  description,
}) => {
  const [currentTab, setCurrentTab] = useState('variations')
  const cancelButtonRef = useRef(null)

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.stopPropagation() // Prevents "Enter" key from propagating outside the modal
    }
  }

  const tabs = [
    {
      name: 'Variations',
      key: 'variations',
      current: currentTab === 'variations',
    },
    {
      name: 'Captions & Music',
      key: 'subtitlesWithMusicAddon',
      current: currentTab === 'subtitlesWithMusicAddon',
    },
    {
      name: 'Edits',
      key: 'secondEditsVersionAddon',
      current: currentTab === 'secondEditsVersionAddon',
    },
    {
      name: 'Aspect Ratio',
      key: 'aspectRationsAddons',
      current: currentTab === 'aspectRationsAddons',
    },
  ]

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as='div'
        className='fixed inset-0 overflow-y-auto'
        initialFocus={cancelButtonRef}
        onClose={setOpen}
        onKeyDown={handleKeyDown} // Attach the onKeyDown handler here
      >
        <div className='flex items-end justify-center min-h-screen pt-4 px-4 text-center sm:block sm:p-0'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </Transition.Child>

          <span
            className='hidden sm:inline-block sm:align-middle sm:h-screen'
            aria-hidden='true'
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div className='inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full'>
              <div className='flex flex-col h-full'>
                {/* <div className='border-b border-gray-200 flex-shrink-0'>
                  <nav className='flex space-x-4'>
                    {tabs.map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setCurrentTab(tab.key)}
                        className={classNames(
                          currentTab === tab.key
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                          'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm',
                        )}
                      >
                        {tab.name}
                      </button>
                    ))}
                  </nav>
                </div> */}
                <div className='block'>
                  <div className='border-b border-gray-200'>
                    <nav
                      className='ml-4 -mb-px flex space-x-4'
                      aria-label='Tabs'
                    >
                      {tabs.map((tab) => (
                        <button
                          key={tab.key}
                          onClick={() => setCurrentTab(tab.key)}
                          className={classNames(
                            tab.current
                              ? 'border-indigo-500 text-indigo-600'
                              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                            'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium',
                          )}
                          aria-current={tab.current ? 'page' : undefined}
                        >
                          {tab.name}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>

                <div className='flex-grow overflow-y-auto'>
                  <div className=''>
                    {currentTab === 'variations' && (
                      <OrderHooks
                        collaboration={collaboration}
                        title={`Buy more variations`}
                        description={`Each variation is an altered version of the original video, featuring a unique hook or call-to-action. The duration of each variation is up to 5 seconds.`}
                        open={open}
                        setOpen={setOpen}
                      />
                    )}
                    {currentTab === 'subtitlesWithMusicAddon' && (
                      <OrderCaptionsAndMusicAddon
                        collaboration={collaboration}
                        addon={collaboration.addons.find(
                          (addon) =>
                            addon.description === CampaignAddon.subtitles ||
                            addon.description === CampaignAddon.subtitlesMusic,
                        )}
                        open={open}
                        setOpen={setOpen}
                      />
                    )}
                    {currentTab === 'secondEditsVersionAddon' && (
                      <OrderSecondEditsVersionAddon
                        collaboration={collaboration}
                        addon={collaboration.addons.find(
                          (addon) =>
                            addon.description ===
                            CampaignAddon.secondEditedVersion,
                        )}
                        open={open}
                        setOpen={setOpen}
                      />
                    )}
                    {currentTab === 'aspectRationsAddons' && (
                      <OrderAspectRatiosAddon
                        collaboration={collaboration}
                        addons={collaboration.addons.filter(
                          (addon) =>
                            addon.description === CampaignAddon.vertical ||
                            addon.description === CampaignAddon.square ||
                            addon.description === CampaignAddon.portrait,
                        )}
                        open={open}
                        setOpen={setOpen}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default OrderAddonsVariationsModal
